import type { TariffKind } from './tariff.types'

export const UserRole = {
    USER: 'USER',
    MARKER: 'MARKER',
    ADMIN: 'ADMIN',
    SUPERADMIN: 'SUPERADMIN',
    SERVICE: 'SERVICE',
} as const

export type UserRole = typeof UserRole[keyof typeof UserRole]

export const AuthProvider = {
    LOCAL: 'LOCAL',
    GOOGLE: 'GOOGLE',
    APPLE: 'APPLE'
} as const
export type AuthProvider = typeof AuthProvider[keyof typeof AuthProvider]

export const ConfirmationChannel = {
    EMAIL: 'EMAIL',
    SMS: 'SMS',
    TELEGRAM: 'TELEGRAM',
    VIBER: 'VIBER'
} as const
export type ConfirmationChannel = typeof ConfirmationChannel[keyof typeof ConfirmationChannel]

export type RegistrationPath = 'EMAIL' | 'PHONE'

export interface User {
    id: number
    email: string | null
    pendingEmail?: string | null
    fullName: string
    phone?: string | null
    role: UserRole
    authProvider?: AuthProvider
    emailVerified?: boolean
    phoneVerified?: boolean
    profileCompleted?: boolean
    birthDate?: string | null
    photoUrl?: string | null
    telegramChatId?: string | null
    telegramUsername?: string | null
    telegramLinked?: boolean
    telegramBotBlocked?: boolean | null
    billNotificationChannel?: string | null
    preferredLocale?: string | null
    isActive: boolean
    bonusBalance?: number
    loyaltyPercent?: number
    birthdayBonusEligible?: boolean
    birthdayBonusBlockReason?: string | null
    /** SERVICE role only: the pool table this account may update scores for. */
    serviceTableId?: number | null
    /** Write-only: new password for SERVICE accounts (never returned by API). */
    newPassword?: string
    military?: boolean
    schoolStudent?: boolean
    schoolStudentActive?: boolean
    specialTariffKind?: TariffKind
    schoolTrainingCount?: number
    schoolMinTrainings?: number
    schoolWindowMonths?: number
    createdAt?: string
    lastLogin?: string | null
}

// Auth DTOs — login with email or phone
export interface LoginRequest {
    emailOrPhone: string
    password: string
}

export interface RegisterRequest {
    email?: string | null
    phone?: string | null
    password: string
    fullName: string
    confirmationChannel?: ConfirmationChannel
    registrationPath?: RegistrationPath
    personalDataConsent?: boolean
}

export interface AuthResponse {
    token: string
    tokenType: string
    expiresIn: number
    user: User
}

// User Management DTOs
export interface UpdateUserRequest {
    fullName?: string
    email?: string
    role?: UserRole
    isActive?: boolean
}

export interface ChangePasswordRequest {
    currentPassword: string
    newPassword: string
}

export type MergeSide = 'LEFT' | 'RIGHT'
export type MergeFieldPick = 'LEFT' | 'RIGHT' | 'NONE'

export type MergeFieldKey =
    | 'fullName'
    | 'email'
    | 'phone'
    | 'birthDate'
    | 'photoUrl'
    | 'role'
    | 'authProvider'
    | 'telegramChatId'
    | 'billNotificationChannel'
    | 'preferredLocale'
    | 'enabled'

export interface UserMergeRequest {
    leftUserId: number
    rightUserId: number
    survivor: MergeSide
    fieldPicks?: Partial<Record<MergeFieldKey, MergeFieldPick>>
}

export interface UserMergeFkCounts {
    leftBookings: number
    rightBookings: number
    leftBillLines: number
    rightBillLines: number
    leftPlayerAssignments: number
    rightPlayerAssignments: number
    leftBonusTransactions: number
    rightBonusTransactions: number
}

export interface UserMergeLoyaltySummary {
    leftBonusBalance: number
    rightBonusBalance: number
    resultBonusBalance: number
    leftRollingSpend12m: number
    rightRollingSpend12m: number
    note: string
}

export interface UserMergePreviewResponse {
    left: User
    right: User
    result: User
    survivor: MergeSide
    survivorUserId: number
    discardedUserId: number
    resolvedPicks: Record<string, MergeFieldPick>
    conflicts: string[]
    optionalFields: string[]
    fkCounts: UserMergeFkCounts
    loyalty: UserMergeLoyaltySummary
}